<script lang="ts">
    import {now} from '$lib/stores/now';
    import {date_pretty, getImageLink} from '$lib/utils';
    import {asLink} from '$lib/utils-svelte';
    import {createEventDispatcher} from 'svelte';
    import {tooltip} from 'svooltip';

    export let supplier: SupplierListItem;
    export let favoriteBtn = false;

    const dispatch = createEventDispatcher<{
        'change-favorite': {value: boolean};
    }>();

    $: url = `/catalog/${supplier.slug}`;
    $: is_top = supplier.top_until > $now.ts;
    $: is_free = supplier.free_until > $now.ts;

    function onChangeFavorite() {
        dispatch('change-favorite', {value: !supplier.is_favorite});
    }
</script>

<div class="py-6 space-y-3 first:pt-0 last:pb-0" data-id={supplier.id}>
    <div class="flex flex-col gap-3 sm:flex-row">
        <div class="w-full h-auto sm:w-24 sm:h-24 grow-0 shrink-0">
            <a href={url} target="_blank">
                <img class="mx-auto w-36 sm:w-full" alt={supplier.title} src={getImageLink(supplier.logo, '144x144rtr')} />
            </a>
        </div>

        <div class="flex flex-col w-full gap-3 grow-1">
            <div class={is_top ? 'px-2 py-1 border-t-2 border-b-2 border-blue bg-blue-50 text-blue text-sm' : ''}>
                <a class="text-xl {is_top ? 'text-blue' : 'text-black'} font-bold" href={url} target="_blank"
                    >{supplier.title}
                    {#if supplier.is_favorite}
                        <svg class="inline fill-purple" height="15" width="17" xmlns="http://www.w3.org/2000/svg"
                            ><path
                                d="M9.043 15.001S-3.891 8.212 3.035 1.537c3.602-1.914 5.946 1.77 5.946 1.77s2.345-3.684 5.947-1.77c6.942 6.675-5.885 13.464-5.885 13.464"
                                fill-rule="evenodd"
                            /></svg
                        >
                    {/if}</a
                >
            </div>

            <div class="flex flex-row flex-wrap gap-4 max-sm:justify-between">
                <button class="link" use:asLink={`http://${supplier.website.domain}`}>{supplier.website.domain}</button>

                {#if favoriteBtn}
                    <button type="button" class="flex items-center justify-end text-black-500 gap-x-2" on:click={() => onChangeFavorite()}>
                        {#if supplier.is_favorite}
                            В избранном. <span class="link ajax">Убрать?</span>
                        {:else}
                            <svg height="15" width="17" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M9.043 15.001S-3.891 8.212 3.035 1.537c3.602-1.914 5.946 1.77 5.946 1.77s2.345-3.684 5.947-1.77c6.942 6.675-5.885 13.464-5.885 13.464"
                                    fill="#06C359"
                                    fill-rule="evenodd"
                                />
                            </svg>
                            <span class="link ajax">В избранное</span>
                        {/if}
                    </button>
                {/if}
            </div>

            {#if supplier.stats?.goods_count || supplier.stats?.goods_pph}
                <div class="flex flex-row items-center w-full max-w-md gap-2 text-sm text-black-500">
                    <span class="cursor-help" title="Число товаров, которое в среднем парсят пользователи" use:tooltip={{delay: [500, 0]}}>
                        <svg xmlns="http://www.w3.org/2000/svg" class="size-5 fill-black-500" viewBox="0 0 24 24">
                            <path
                                d="M6.5 22a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9Zm11 0a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9Zm-11-11a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9Zm11 0a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9Zm0-2a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Zm-11 0a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Zm11 11a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Zm-11 0a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"
                            />
                        </svg>
                    </span>
                    <b>{supplier.stats.goods_count}</b>

                    <span>&nbsp;</span>

                    <span
                        class="cursor-help"
                        title="Число товаров, которое в среднем парсится за час реального времени"
                        use:tooltip={{delay: [500, 0]}}
                    >
                        <svg class="size-5 fill-black-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 48 48">
                            <path
                                class="stroke-black-600"
                                stroke-linejoin="round"
                                stroke-width="4"
                                d="M30.297 18.779s-3.23 9.102-4.764 10.69a4 4 0 1 1-5.754-5.556c1.534-1.59 10.518-5.134 10.518-5.134Z"
                            ></path>
                            <path
                                stroke="currentColor"
                                fill="none"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="4"
                                d="M38.85 38.85C42.65 35.048 45 29.798 45 24c0-11.598-9.402-21-21-21S3 12.402 3 24c0 5.799 2.35 11.049 6.15 14.85M24 4v4M38.845 11.142l-3.108 2.517M42.523 27.233l-3.898-.9M5.477 27.233l3.898-.9M9.155 11.142l3.108 2.517"
                            ></path>
                        </svg>
                    </span>
                    <b>{supplier.stats.goods_pph}</b>
                </div>
            {/if}

            <div class="flex overflow-hidden gap-x-3 whitespace-nowrap text-ellipsis">
                {#each supplier.category_list.slice(0, 2) as category (category.slug)}
                    <span class="px-2 py-1 text-sm border border-gray bg-black-50">
                        {category.title}
                    </span>
                {/each}

                {#if supplier.category_list.length > 2}
                    <span class="px-2 py-1 text-sm border border-gray bg-black-50">
                        +{supplier.category_list.length - 2}
                    </span>
                {/if}
            </div>

            <div class="w-full max-w-md overflow-hidden text-sm text-black-500 whitespace-nowrap text-ellipsis">
                {supplier.city_list.map(v => v.title).join(', ')}
            </div>

            <div>
                <a data-tour="catalog-parser-start" class="btn btn-green h-14 !flex-col" href="/my/parser/{supplier.website.domain}"
                    >Начать загрузку
                    {#if is_free}
                        <br />
                        <span class="text-sm">
                            <b>Бесплатно</b> до {date_pretty(supplier.free_until)}
                        </span>
                    {/if}</a
                >
            </div>
        </div>
    </div>
</div>
